import { useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * Phase-2 bottom-strip card payloads. All shapes mirror the generated GraphQL
 * types (backend-workspace/apps/gripp-api/generated/index.ts) — schema-first, no
 * hand-maintained shapes that drift. The Activity card has its own query
 * (useHomeActivitySummary); these are the five other cards.
 */
export interface HomeAssetStatusSlice {
  status: string;
  count: number;
}
export interface HomeAssetsCard {
  total: number;
  items: HomeAssetStatusSlice[];
}
export interface HomeRoutineRow {
  id: string;
  name: string;
  completionRate: number;
}
export interface HomeRoutinesCard {
  total: number;
  items: HomeRoutineRow[];
}
export interface HomeProductRow {
  name: string;
  units: number;
  percentage: number;
}
export interface HomeProductsCard {
  total: number;
  items: HomeProductRow[];
}
export interface HomeCampaignRow {
  name: string;
  sent: number;
  responded: number;
}
export interface HomeCampaignsCard {
  total: number;
  items: HomeCampaignRow[];
}
export interface HomeCustomersCard {
  total: number;
  active: number;
  pending: number;
  new: number;
}
export interface HomeCards {
  assets: HomeAssetsCard;
  routines: HomeRoutinesCard;
  products: HomeProductsCard;
  campaigns: HomeCampaignsCard;
  customers: HomeCustomersCard;
}

const HOME_CARDS_QUERY = `
  query HomeCards($filter: HomeActivityFilter) {
    homeCards(filter: $filter) {
      assets {
        total
        items { status count }
      }
      routines {
        total
        items { id name completionRate }
      }
      products {
        total
        items { name units percentage }
      }
      campaigns {
        total
        items { name sent responded }
      }
      customers {
        total
        active
        pending
        new
      }
    }
  }
`;

/**
 * Fetches the aggregated bottom-strip card payloads for the current workspace
 * and the selected time range. Single query feeding all five cards — each card
 * component calls this hook and reads its own slice (React Query dedupes to one
 * request per cacheKey).
 *
 * Root query — reads `data.homeCards` directly (no unwrap). Guards
 * `currentWorkspace?.id` (undefined on first render). MOCK-backed in Phase 2.
 */
export function useHomeCards(range: TimeRange): {
  data: HomeCards | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homeCards', workspaceId, range],
    query: HOME_CARDS_QUERY,
    variables: {
      filter: {
        // Scope to the current (possibly impersonated) workspace — the query is
        // gated on workspaceId below, so it is always present here. Without it
        // the backend leaks global counts under workspace-only impersonation
        // (GRIPP #2859).
        workspaceId,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    },
    options: {
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  });

  return { data: data?.homeCards, isLoading, error: error as Error | null };
}
